import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, MenuItem, Paper, Select, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import { FaArrowLeft } from "react-icons/fa6";
import { useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "../../../../Components/BreadCrumb";
import { useLazyGetSupplierValidationLogsQuery } from "../../../../Services/SupplierService";

/**
 * Every credential validation ever attempted, across every supplier.
 *
 * This log first lived only on a sub-supplier's own page — which you can only
 * reach once that sub-supplier EXISTS. A validation that fails creates nothing,
 * so the refusals, the entire reason for keeping a log, had nowhere to be seen
 * from: the tab showed failures only for accounts that eventually succeeded.
 *
 * Reached from a button beside Add Supplier, because the moment you want it is
 * the moment a supplier would not go in.
 */

const when = (d?: string) =>
  d
    ? new Date(d).toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

const duration = (ms?: number) => {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  return s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};

/** Suppliers sometimes return whole HTML pages as errors. Show the point. */
const readable = (text?: string) => {
  if (!text) return "";
  const raw = String(text);
  if (/<html|<!DOCTYPE/i.test(raw)) {
    const prefix = raw.match(/^\s*([^<]{1,60}?)\s*(?=<)/)?.[1]?.trim();
    const title = raw.match(/<title[^>]*>([^<]+)<\/title>/i)?.[1]?.trim();
    if (prefix || title) return [prefix, title].filter(Boolean).join(" ");
  }
  return raw
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Refused and unreachable are different things.
 *
 * "Refused" is the supplier's decision — credentials they have not enabled yet,
 * typically — and is something to take back to them. "Could not reach" is ours
 * to chase. Telling a supplier their credentials were rejected when in fact we
 * never got an answer wastes everybody's time.
 */
const OUTCOME: Record<string, { bg: string; fg: string; label: string }> = {
  valid:   { bg: "rgba(46,125,50,0.12)", fg: "#2e7d32", label: "Accepted" },
  invalid: { bg: "rgba(211,47,47,0.12)", fg: "#c62828", label: "Refused" },
  error:   { bg: "rgba(237,108,2,0.12)", fg: "#ed6c02", label: "Could not reach" },
};

const SupplierValidationLogs: React.FC = () => {
  const navigate = useNavigate();

  /**
   * Arrive pre-filtered.
   *
   * The supplier page links here with its own account id, so "show me this
   * account's history" lands already scoped instead of making someone retype
   * it. The filtering itself lives only here -- a tab on the supplier page
   * duplicated it, and filtered by the PARENT supplier, so it quietly listed
   * sibling accounts' attempts as if they belonged to the one on screen.
   */
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [outcome, setOutcome] = useState("all");
  const [search, setSearch] = useState(initialQuery);
  const [debounced, setDebounced] = useState(initialQuery);
  const [rows, setRows] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const [fetchLogs] = useLazyGetSupplierValidationLogsQuery();

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchLogs({ page: page + 1, pageSize, outcome, search: debounced })
      .unwrap()
      .then((res: any) => {
        if (cancelled) return;
        setRows(res?.data || []);
        setTotalRecords(res?.totalRecords || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setRows([]);
        setTotalRecords(0);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [page, pageSize, outcome, debounced, fetchLogs]);

  return (
    <Box sx={{ p: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Breadcrumb
          name="Supplier Validation Logs"
          items={[{ label: "Suppliers" }, { label: "Validation Logs" }]}
        />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search supplier, account or message"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          <Select
            size="small"
            value={outcome}
            onChange={(e) => { setOutcome(e.target.value); setPage(0); }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All outcomes</MenuItem>
            <MenuItem value="valid">Accepted</MenuItem>
            <MenuItem value="invalid">Refused</MenuItem>
            <MenuItem value="error">Could not reach</MenuItem>
          </Select>
          <Button
            variant="outlined"
            startIcon={<FaArrowLeft />}
            onClick={() => navigate("/supplier")}
            sx={{ textTransform: "none" }}
          >
            Back to Suppliers
          </Button>
        </Stack>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Every credential check made against a supplier&rsquo;s API, including the
        ones that were refused and never became an account. Credentials
        themselves are never stored — only a fingerprint of each: its length and
        last four characters, which is enough to identify a key with the
        supplier without it being usable here.
      </Typography>

      <Paper>
        <TableContainer sx={{ maxHeight: "62vh" }}>
          {/* Fixed layout with explicit widths. Auto layout let the long
              credential list push into the neighbouring column instead of
              ellipsing, which is what ran "USERNAME, PASSWORD, TERMINALID"
              straight into the By column. */}
          <Table size="small" stickyHeader sx={{ tableLayout: "fixed", minWidth: 1280 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 170 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 110 }}>Supplier</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 150 }}>Sub Supplier</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 130 }}>Outcome</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 300 }}>What the supplier said</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 280 }}>Credentials supplied</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 180 }}>By</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 90, textAlign: "right" }}>Took</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      No credential checks recorded yet.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Attempts are recorded from the moment this log was added,
                      so checks made before then do not appear.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((row: any) => {
                  const style = OUTCOME[row.outcome] || OUTCOME.error;
                  const said = readable(row.message);
                  return (
                    <TableRow key={row._id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{when(row.attemptedAt)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 500 }}>{row.supplierId}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{row.subSupplierId || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={style.label}
                          sx={{ backgroundColor: style.bg, color: style.fg, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ overflow: "hidden" }}>
                        {said ? (
                          <Tooltip title={said}>
                            <Typography variant="body2" noWrap>{said}</Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ overflow: "hidden" }}>
                        {/*
                        A fingerprint, never the credential: length and last
                        four. Enough to tell whether the same key was tried
                        twice or something truncated was pasted, and enough for
                        the supplier to identify it on their side.
                      */}
                      {(() => {
                        const prints = row.credentialFingerprints?.length
                          ? row.credentialFingerprints.map(
                              (f: any) => `${f.field} ${f.length} chars ···${f.last4}`,
                            )
                          : row.credentialFieldsProvided || [];
                        if (!prints.length)
                          return <Typography variant="caption" color="text.disabled">none</Typography>;
                        return (
                          <Tooltip title={prints.join("  ·  ")}>
                            <Typography variant="caption" noWrap sx={{ color: "text.secondary", display: "block" }}>
                              {prints.join(", ")}
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                      <TableCell sx={{ overflow: "hidden" }}>
                        <Tooltip title={row.triggeredByEmail || row.triggeredBy || ""}>
                          <Typography variant="caption" noWrap sx={{ color: "text.secondary", display: "block" }}>
                            {row.triggeredByName || row.triggeredByEmail || "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {duration(row.durationMs)}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default SupplierValidationLogs;
